import React from 'react';
import { Button, EmptyContent } from '@/components/ui';
import ErrorLayout from '@/components/ErrorLayout';
import { IconAlertTriangle } from '@tabler/icons-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch() {}

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorLayout>
          <EmptyContent
            state="error"
            title="Something went wrong"
            description="An unexpected error occurred. Please try again later."
            icon={<IconAlertTriangle />}
            withIconBox
          >
            <Button
              onClick={this.handleReload}
              variant="classic"
              color="secondary"
            >
              Reload Page
            </Button>
            {this.state.error && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: 'var(--color-background-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--color-error)',
                  }}
                >
                  Error Details:
                </div>
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {this.state.error.message}
                </div>
              </div>
            )}
          </EmptyContent>
        </ErrorLayout>
      );
    }

    return this.props.children;
  }
}
